import AuthContentWrapper from "./components/AuthContentWrapper";
import { C2Text, T1Text } from "../../common/components/text/CustomText";
import { Column, SizedBox } from "../../common/components/CommonBaseComponents";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";
import CustomPopup from "../../common/components/popup/CustomPopup";
import { Theme } from "../../common/Theme";
import axios from "axios";
import CryptoJS from "crypto-js";
import LocalStorageKey from "../../common/enums/LocalStorageKey";
import LMPAuthApi from "../../api/lmp/LMPAuthApi";
import LMPAuthUtils from "../../api/lmp/LMPAuthUtils";
import Role from "../../common/enums/Role";
import UserService from "../../service/UserService";

const stringify = (params) =>
    Object.entries(params)
        .map(([k, v]) => encodeURIComponent(k) + "=" + encodeURIComponent(v))
        .join("&");

const getDateTime = (oauth2url) => {
    const headers = {
        Accept: "application/json",
        "x-lge-appKey": "a4209c397c0f4999945ed7f4b6e86578",
        "Content-Type": "application/x-www-form-urlencoded",
        "x-message-id": Math.floor(Math.random() * 10000000000 + 1),
    };

    const options = {
        url: oauth2url + "oauth/1.0/datetime",
        method: "GET",
        headers: headers,
    };

    return axios(options).then((res) => res.data.date);
};

const getSignature = (path, datetime, params) => {
    const requestParams = stringify(params);
    const stringToSign = path + "?" + requestParams + "\n" + datetime;
    console.log(stringToSign);
    return CryptoJS.enc.Base64.stringify(CryptoJS.HmacSHA1(stringToSign, "256d006c06374d15bfd767ecf49229fb"));
};

const requestToken = (oauth2url, code, datetime) => {
    const params = {
        code: code,
        grant_type: "authorization_code",
        // 인증 요청 시 사용한 redirect_uri 와 반드시 동일해야 함 (OAuth 규칙)
        redirect_uri: LMPAuthUtils.getRedirectUri(),
    };

    const headers = {
        Accept: "application/json",
        "x-lge-oauth-signature": getSignature("/oauth/1.0/oauth2/token", datetime, params),
        "x-lge-oauth-date": datetime,
        "x-lge-appKey": "a4209c397c0f4999945ed7f4b6e86578",
        "x-message-id": Math.floor(Math.random() * 10000000000 + 1),
        "Content-Type": "application/x-www-form-urlencoded",
    };

    const options = {
        url: oauth2url + "oauth/1.0/oauth2/token",
        method: "POST",
        headers: headers,
        params: params,
        data: {},
    };

    return axios(options).then((res) => res.data);
};

// 인증/세션 관련 키만 제거하고 server(국가)는 보존 → 이후 LMP 재접속 시 처음 국가/언어 유지
const clearAuthStorage = () => {
    [
        LocalStorageKey.accessToken,
        LocalStorageKey.refreshToken,
        LocalStorageKey.role,
        LocalStorageKey.userNo,
        LocalStorageKey.displayUserID,
        LocalStorageKey.lgmSessionId,
        LocalStorageKey.oauth2BackendUrl,
    ].forEach((key) => localStorage.removeItem(key));
};

function LoginRedirectPage() {
    const navigate = useNavigate();
    const api = new LMPAuthApi();
    const userService = new UserService();
    const [searchParams] = useSearchParams();
    const state = searchParams.get("state");
    const result = searchParams.get("result");
    const userCountry = searchParams.get("user_country");
    const language = searchParams.get("language");
    const oauth2BackendUrl = searchParams.get("oauth2_backend_url");
    const userId = searchParams.get("user_id");
    const code = searchParams.get("code");
    const userNumber = searchParams.get("user_number");

    useEffect(() => {
        console.log("state: ", state);
        console.log("result: ", result);
        console.log("userCountry: ", userCountry);
        console.log("language: ", language);
        console.log("oauth2BackendUrl: ", oauth2BackendUrl);
        console.log("code: ", code);
        console.log("userNumber: ", userNumber);
        // 로그인 취소(LMP 뒤로가기) 등으로 정상 code/oauth2BackendUrl 없이 진입한 경우 토큰 교환 스킵
        if (!code || !oauth2BackendUrl) {
            navigate("/auth/init");
            return;
        }
        localStorage.setItem(LocalStorageKey.displayUserID, userId);
        localStorage.setItem(LocalStorageKey.userNo, userNumber);
        getDateTime(oauth2BackendUrl).then((res) => {
            return requestToken(oauth2BackendUrl, code, res).then((res) => {
                return api.getUserInfo({ loginSessionID: res.access_token }).then((data) => {
                    const userRoles = data.account.usrRoleInfo;
                    localStorage.setItem(LocalStorageKey.accessToken, res.access_token);
                    localStorage.setItem(LocalStorageKey.refreshToken, res.refresh_token);
                    console.log("userRoles: ", userRoles);
                    return userService.getUserInfo().then((data) => {
                        console.log("data: ", data);
                        if (data.franchise_join_status === "pending") {
                            navigate("/auth/role-choice");
                        } else if (userRoles.rjctRoleId !== "") {
                            navigate("/auth/role-message?title=Role Approval Rejected");
                        } else if (userRoles.pendApprRoleId !== "") {
                            navigate("/auth/role-message?title=Sign in unavailable");
                        } else {
                            console.log("userRoles: ", userRoles.lastRoleId);
                            if (userRoles.lastRoleId === "USR037001") {
                                clearAuthStorage();
                                navigate("/auth/move-app");
                                return;
                            }
                            if (userRoles.lastRoleId === "ADM037001") {
                                localStorage.setItem(LocalStorageKey.role, Role.Admin);
                            } else if (userRoles.lastRoleId.endsWith("01")) {
                                localStorage.setItem(LocalStorageKey.role, Role.FranchiseManager);
                            } else if (userRoles.lastRoleId.endsWith("02")) {
                                localStorage.setItem(LocalStorageKey.role, Role.CallCenter);
                            } else if (userRoles.lastRoleId.endsWith("03")) {
                                // localStorage.setItem(LocalStorageKey.role, Role.StoreManager);
                                clearAuthStorage();
                                navigate("/auth/move-app");
                                return;
                            }
                            navigate("/");
                        }
                    });
                });
            });
        }).catch(() => navigate("/auth/init"));
    }, [state, result, userCountry, language, oauth2BackendUrl, code, userNumber, userId]);

    // error popup
    const [isPopupOpened, setIsPopupOpened] = useState(false);
    const [popupMessage, setPopupMessage] = useState("");

    return (
        <AuthContentWrapper contentPadding={"62px 60px"}>
            <Column justifyContent={"center"}>
                <T1Text letterSpacing={"-0.56px"} fontWeight={"500"}>
                    Laundry Crew Admin
                </T1Text>
                <SizedBox height={"10px"} />
                <C2Text fontColor={Theme.color.gray500}>Login Success</C2Text>
                <SizedBox height={"44px"} />
                <C2Text fontColor={Theme.color.gray500} textAlign={"center"}>
                    Processing Login
                </C2Text>
            </Column>
            <CustomPopup isOpen={isPopupOpened} onConfirm={() => setIsPopupOpened(false)} message={popupMessage} />
        </AuthContentWrapper>
    );
}

export default LoginRedirectPage;
